import { DRAG_HANDLE_CLASS, IGNORE_TARGET_CLASS, SELECTO_TARGET_CLASS } from 'constants/bounding-box';
import { useEventListener } from 'hooks/useEventListener';
import { setActiveWidget, store, useAppDispatch } from 'modules/Editor/store';
import { setWidgetSettingsView } from 'modules/Editor/store/editorSettingsSlice';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import {
  ActiveWidgetIds,
  ActiveWidgetSelectFrom,
  setActiveWidgetSelectFrom,
} from 'modules/Editor/store/widgetControlSlice';
import { selectActiveWidgetIds, selectActiveWidgets, selectCropId } from 'modules/Editor/store/widgetSelector';
import { memo, useRef, useState } from 'react';
import Selecto, { OnDragStart, OnScroll, OnSelect, OnSelectEnd } from 'react-selecto';
import { WidgetType } from 'types/widget.types';
import { detectPortContainer } from 'utils/dom';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';

import { useFocus } from '../Focus';
import { SCROLL_CONTAINER_ID } from '../PageArea/PageArea.config';
import { SIDE_MENU_WIDGET_TYPES } from '../SideMenuSettings/SideMenuSettings.config';
import { getLockedWidgetEl, getWidgetTargetIds } from './BoundingBox.helpers';
import { SelectoTarget } from './BoundingBox.types';
import { getSelectedWidgetTargetEls } from './WidgetSelect.helpers';

interface WidgetSelectProps {
  moveableRef: any;
  selectoRef: React.MutableRefObject<Selecto | null>;
  isDragAreaSelectRef: React.MutableRefObject<boolean>;
  setTargets: (elements: HTMLElement[]) => void;
}

// the margin to the edges where select to scroll would trigger
const SELECT_SCROLL_THRESHOLD = 30;
const SELECT_SCROLL_THROTTLE = 100;
const SELECT_SCROLL_STEP = 40;

// elements with these classes cause the widget selection and dragstart to trigger
// The custom drag handle is rendered outside of the widget and without selection it will unselect the active swidget
const SELECTABLE_TARGETS = [`.${SELECTO_TARGET_CLASS}`, `.${DRAG_HANDLE_CLASS}`];

// Handles widget selection:
//  - on click: selects the widgets
//  - mousedown + drag: area selects the widgets
export const WidgetSelect = memo(({ moveableRef, selectoRef, setTargets, isDragAreaSelectRef }: WidgetSelectProps) => {
  const { pageScrollContainerRef, isCanvasFocused, blur } = useFocus();
  const dispatch = useAppDispatch();
  const mouseDown = useRef(false);
  // store the activeWidgetSelectFrom for the onSelectEnd event from the onSelect event
  const activeWidgetSelectFromRef = useRef<ActiveWidgetSelectFrom>('');
  const [hasContinueSelect, setHasContinueSelect] = useState(false);

  const isSelectDisabled = () => {
    const cropId = selectCropId(store.getState());
    return !cropId;
  };

  const onMouseDown = () => {
    mouseDown.current = true;
  };

  const onMouseUp = () => {
    mouseDown.current = false;
  };

  const onKeyUp = (e: Event) => {
    if ((e as KeyboardEvent).key === 'Shift') {
      setHasContinueSelect(false);
    }
  };

  const onKeyDown = (e: Event) => {
    if ((e as KeyboardEvent).key === 'Shift') {
      setHasContinueSelect(true);
    }
  };

  useEventListener('mousedown', onMouseDown);
  useEventListener('mouseup', onMouseUp);

  // this is to prevent the shift key from getting stuck
  // Selecto does not trigger the keyup event when the shift key is released always - esp with screen recorders
  useEventListener('keyup', onKeyUp);
  useEventListener('keydown', onKeyDown);

  const setActiveWidgets = (targets: SelectoTarget[], selectType?: ActiveWidgetSelectFrom): void => {
    const selectedIds: ActiveWidgetIds = getWidgetTargetIds(targets);
    const activeWidgetIds = selectActiveWidgetIds(store.getState());

    if (!selectedIds.length && !activeWidgetIds.length) return;
    // Custom drag handles for widgets should not reset the active widget
    if (targets[0]?.classList.contains(DRAG_HANDLE_CLASS)) return;

    dispatch(setActiveWidget(selectedIds));
    if (selectType) dispatch(setActiveWidgetSelectFrom(selectType));
  };

  const onDragStart = (e: OnDragStart): void => {
    const portContainer = detectPortContainer(e.clientX, e.clientY);
    if (portContainer) return e.stop();

    const moveable = moveableRef.current;
    const target = e.inputEvent.target;
    const isShift = e.inputEvent.shiftKey;

    const isMoveable = moveable.isMoveableElement(target);
    if (isMoveable) return e.stop();

    // Prevent deselect when clicking on ignore target
    if (target.closest(`.${IGNORE_TARGET_CLASS}`)) return e.stop();

    const activeWidgets = selectActiveWidgets(store.getState());
    const hasGroupSelected = activeWidgets.length && activeWidgets.filter((aW) => !!aW.groupMembers.length).length > 0;

    if (!hasGroupSelected) return;
    if (isShift) return;

    // TODO: improve this
    // Selecto blocks click events for group targets (= widgets).
    // This is a workaround to allow click events to be passed to group member widgets
    // and at the same time select dragging from widgets in a group.

    // Safari doesn't have path
    const inputPath = e.inputEvent.path || (e.inputEvent.composedPath && e.inputEvent.composedPath()) || [];

    for (const el of inputPath) {
      if (el && el.id && groupIdCache.hasParentWidget(el.id)) {
        dispatch(setActiveWidget(el.id));
        // see https://github.com/daybrush/moveable/issues/481
        setTimeout(() => {
          // e.isDragStart is true for on quick
          // check if the mouse is still down before triggering drag start of widget
          if (!mouseDown.current) return;
          moveable.dragStart(e.inputEvent);
        }, 0);

        return;
      }
    }
  };

  /**
   * Manually set locked widgets as active as they are not selecto-targets to allow for drag-area selection
   *
   * @param lockedWidgetEl
   * @param activeWidgetSelectFrom
   */
  const setLockedWidgetActive = (lockedWidgetEl: Element, activeWidgetSelectFrom: ActiveWidgetSelectFrom) => {
    const state = store.getState();
    const activeWidgets = selectActiveWidgets(state);

    const lockedWidgetId = lockedWidgetEl.id;
    const hasLockedWidgetActive = activeWidgets.length === 1 && activeWidgets[0].id === lockedWidgetId;

    // if the locked widget is already selected and its clicked again -> do nothing
    if (hasLockedWidgetActive) return;

    // the locked widget was clicked -> select it
    dispatch(setActiveWidget(lockedWidgetId));
    dispatch(setActiveWidgetSelectFrom(activeWidgetSelectFrom));
    return;
  };

  const onSelectStart = (e: OnSelect) => {
    if (!e.inputEvent.shiftKey && hasContinueSelect) {
      selectoRef.current?.setSelectedTargets([]);
      setHasContinueSelect(false);
    }

    // reset the click ref
    activeWidgetSelectFromRef.current = '';

    // if its a mousedown drag select -> unset the active widgets
    if (!e.isDragStartEnd) setActiveWidgets([], '');
  };

  const onSelect = (e: OnSelect): void => {
    // if the input event is a mousedown, it counts as a "click" widget select
    const activeWidgetSelectFrom = e.inputEvent?.type === 'mousedown' ? 'click-select' : '';
    const isSelected = e.selected.length > 0;

    // Check for locked widgets and select them
    // locked widgets can only be selected by clicking - not from the drag area
    if (!isSelected && activeWidgetSelectFrom === 'click-select') {
      const lockedWidgetEl = getLockedWidgetEl(e);
      if (lockedWidgetEl) return setLockedWidgetActive(lockedWidgetEl, activeWidgetSelectFrom);
    }

    // for the drag handle select event -> start dragging the widget directly no need to set the targets
    if (
      isSelected &&
      activeWidgetSelectFrom === 'click-select' &&
      e.selected.length === 1 &&
      e.selected[0].classList.contains(DRAG_HANDLE_CLASS)
    ) {
      return;
    }

    // store the activeWidgetSelectFrom for the onSelectEnd event
    activeWidgetSelectFromRef.current = activeWidgetSelectFrom;

    const state = store.getState();

    // if the canvas is focused, blur it while drag-selecting
    if (isCanvasFocused()) blur();
    if (!isDragAreaSelectRef.current) isDragAreaSelectRef.current = true;

    // if there are active widgets reset them - for locked widgets the onSelectStart wont always trigger
    if (selectActiveWidgetIds(state)?.length) setActiveWidgets([], '');

    const targets = getSelectedWidgetTargetEls(e.selected as HTMLElement[], selectWidgets(state), groupIdCache);
    setTargets(targets);
  };

  const onSelectEnd = (e: OnSelectEnd): void => {
    // reset the drag area select flag
    isDragAreaSelectRef.current = false;

    setActiveWidgets(e.selected, activeWidgetSelectFromRef.current);

    const moveable = moveableRef.current;
    const isSingleSelected = e.selected.length === 1 && moveable.refTargets.length === 1;

    // For the custom drag handle select event -> start dragging the widget directly
    if (isSingleSelected && e.selected[0].classList.contains(DRAG_HANDLE_CLASS)) {
      return moveable.dragStart(e.inputEvent);
    }

    // Case 1: If single selected id and single moveable ref target id are different, active the single selected id
    // Case 2: Deselect if no element is selected
    const hasMatch = isSingleSelected && e.selected[0].id === moveable.refTargets[0].id;
    const isSelected = e.selected?.length > 0;

    // Check for locked widgets and select them
    // locked widgets can only be selected by clicking - not from the drag area
    // if the selecto-area is 0px x 0px, it counts as a "click" widget select
    const activeWidgetSelectFrom = e.rect.width === 0 && e.rect.height === 0 ? 'click-select' : 'drag-area';
    if (!isSelected && activeWidgetSelectFrom === 'click-select') {
      const lockedWidgetEl = getLockedWidgetEl(e);
      if (lockedWidgetEl) {
        setLockedWidgetActive(lockedWidgetEl, activeWidgetSelectFrom);
        const widgetType = getWidgetTypeFromId(lockedWidgetEl.id);
        // onDoubleClick on widget base is not triggered on first render,
        // so we need to manually dispatch the action here to open the widget settings.
        // Image widget is an exception as the problem only happen to chart widgets.
        if (e.isDouble && SIDE_MENU_WIDGET_TYPES.includes(widgetType) && widgetType !== WidgetType.Image) {
          dispatch(setWidgetSettingsView(true));
        }
        return;
      }
    }

    if ((isSingleSelected && !hasMatch) || !isSelected) {
      setActiveWidgets(e.selected, activeWidgetSelectFrom);
    }

    if (e.isDragStart) {
      e.inputEvent.preventDefault();

      // see https://github.com/daybrush/moveable/issues/481
      setTimeout(() => {
        // e.isDragStart is true for on quick
        // check if the mouse is still down before triggering drag start of widget
        if (!mouseDown.current) return;
        moveable.dragStart(e.inputEvent);
      }, 0);
    }
  };

  // cast pageScrollContainerRef?.current type from HTMLDIVElement to HTMLElement to match the type of scrollOptions
  const pageScrollContainer = pageScrollContainerRef?.current as HTMLElement;
  const scrollOptions = {
    container: pageScrollContainer,
    throttle: SELECT_SCROLL_THROTTLE,
    threshold: SELECT_SCROLL_THRESHOLD,
  };

  const onScroll = (e: OnScroll): void => {
    if (pageScrollContainerRef?.current) {
      pageScrollContainerRef.current.scrollBy(e.direction[0] * SELECT_SCROLL_STEP, e.direction[1] * SELECT_SCROLL_STEP);
    }
  };

  // we reset this to null when shift key is released
  const toggleContinueSelect = hasContinueSelect ? 'shift' : null;

  return (
    <Selecto
      ref={selectoRef}
      dragContainer={`#${SCROLL_CONTAINER_ID}`}
      selectableTargets={SELECTABLE_TARGETS}
      toggleContinueSelect={toggleContinueSelect}
      hitRate={0}
      ratio={0}
      selectByClick={true}
      selectFromInside={false}
      onDragStart={onDragStart}
      onSelectStart={onSelectStart}
      onSelect={onSelect}
      onSelectEnd={onSelectEnd}
      scrollOptions={scrollOptions}
      onScroll={onScroll}
      dragCondition={isSelectDisabled}
    />
  );
});
WidgetSelect.displayName = 'WidgetSelect';
